import { GroupingRule } from '@ardoq/api-types';
import { TraverseOptions } from '@ardoq/graph';
import { GraphModelShape } from '@ardoq/data-model';
import type { ScenarioRelatedComponentIds } from 'streams/components/components$';
import {
  TRAVERSE_ALL_BASE,
  TRAVERSE_PARENTS_REFTYPES,
} from 'modelInterface/graph/consts';
import { CommonDropdownOptions } from '@ardoq/global-consts';
import { hasGroupByParentAll } from 'modelInterface/graph/utils';
import { getTraversalConfiguration } from 'tabview/graphViews/traversalViewModel';
import { ViewSettingsTraverseConfig } from './types';
import { DependencyMapViewSettings } from '@ardoq/dependency-map';
import { isTruthy } from '@ardoq/common-helpers';

// #region view settings traverse config
const TRAVERSE_ALL_CHILDREN: TraverseOptions = {
  ...TRAVERSE_ALL_BASE,
  incomingReferenceTypes: new Set(),
  outgoingReferenceTypes: TRAVERSE_PARENTS_REFTYPES,
  excludedComponentIds: new Set(),
};
const TRAVERSE_ALL_PARENTS_AND_CHILDREN: TraverseOptions = {
  ...TRAVERSE_ALL_BASE,
  incomingReferenceTypes: TRAVERSE_PARENTS_REFTYPES,
  outgoingReferenceTypes: TRAVERSE_PARENTS_REFTYPES,
  excludedComponentIds: new Set(),
};
const traverseReferenceChildren = (
  degreesOfChildhood: number
): TraverseOptions => ({
  maxDegrees: 0,
  maxDegreesIncoming: 0,
  maxDegreesOutgoing: degreesOfChildhood === Infinity ? 99 : degreesOfChildhood,
  isParentRelationAsReference: true,
  incomingReferenceTypes: new Set(),
  outgoingReferenceTypes: new Set([CommonDropdownOptions.PARENT_AS_REFERENCE]),
  excludedComponentIds: new Set(),
});

type GetViewSettingsTraverseConfigArgs = {
  supportedUserDefinedGroupingRules: GroupingRule[];
  viewSettings: DependencyMapViewSettings;
  graphModel: GraphModelShape;
  scenarioRelatedComponentIds: ScenarioRelatedComponentIds;
  degreesOfChildhood: number;
};
export const getViewSettingsTraverseConfig = ({
  supportedUserDefinedGroupingRules,
  viewSettings,
  graphModel,
  scenarioRelatedComponentIds,
  degreesOfChildhood,
}: GetViewSettingsTraverseConfigArgs): ViewSettingsTraverseConfig => {
  const { traverseOptions: userTraverseOptions } = getTraversalConfiguration({
    viewSettings,
    graphModel,
    scenarioRelatedComponentIds,
  });

  const hasUserDefinedGrouping = supportedUserDefinedGroupingRules.length > 0;

  const referenceTraverseOptions = { ...userTraverseOptions, maxDegrees: 0 };

  const baseTraverseOptions = hasUserDefinedGrouping
    ? hasGroupByParentAll(supportedUserDefinedGroupingRules)
      ? TRAVERSE_ALL_PARENTS_AND_CHILDREN // requirement: if the user has provided a ParentAll grouping rule, traverse all parents and children
      : undefined // if the user has provided grouping without GBPA, do not traverse parents or children, because these things can interfere with the user's grouping.
    : TRAVERSE_ALL_CHILDREN; // requirement: if the user has not provided a grouping rule, traverse all children

  const sequentialTraverseOptions = [
    referenceTraverseOptions,
    degreesOfChildhood ? traverseReferenceChildren(degreesOfChildhood) : null,
  ].filter(isTruthy);

  return {
    referenceTraverseOptions,
    baseTraverseOptions,
    userTraverseOptions,
    sequentialTraverseOptions,
  };
};

// #endregion view settings traverse config
